import classNames from "classnames";
import React, {FC, memo} from "react";
import {ScheduledPaymentType} from "../../generated/graphql";
import i18n from "../../services/i18n";
import {RenderAmount} from "../TableColumnRenderers/TableColumnRenderers";
import css from "./BookingPriceOverview.less";

/**
 * BookingPriceOverview — wiederverwendbare Preisübersicht für Buchungen.
 *
 * Zeigt die Aufschlüsselung: Buchungspreis (Original) → Mitgliederrabatt
 * → Rabatt → Verrechnetes Guthaben → Zusatzposten → Buchungspreis.
 *
 * Wird gerendert NUR wenn mindestens eine Vergünstigung oder ein
 * Zusatzposten vorhanden ist (sonst sc-3028 Folge: Anzeige wäre redundant
 * zur einfachen "Buchungspreis = X €"-Zeile).
 *
 * Extrahiert aus PanelCardPaymentData.tsx (sc-3029) für Wiederverwendung im
 * "Gebuchte Einheiten anpassen"-Modal beidseitig (Bisherige Buchung +
 * Vorschau der geänderten Buchung).
 */

type ScheduledPaymentLike = {
    id: string;
    type: ScheduledPaymentType | string | null;
    paymentAmount: number;
    title?: string | null;
};

type BookingPriceOverviewProps = {
    grossPrice?: number | null;
    discount?: number | null;
    discountName?: string | null;
    memberDiscount?: number | null;
    creditedBalance?: number | null;
    scheduledPayments?: Array<ScheduledPaymentLike> | null;
    /**
     * Renderingsmodus: "card" füllt einen vorhandenen Container (z.B.
     * PanelCard) und nutzt die ursprünglichen inline-Styles. "compact" ist
     * für kleinere Einbettungen (Adjust-Modal-Spalten) optimiert.
     */
    variant?: "card" | "compact";
    className?: string;
};

const BookingPriceOverview: FC<BookingPriceOverviewProps> = ({
    grossPrice,
    discount,
    discountName,
    memberDiscount,
    creditedBalance,
    scheduledPayments,
    variant = "card",
    className,
}) => {
    const safeGross = grossPrice ?? 0;
    const safeDiscount = discount ?? 0;
    const safeMember = memberDiscount ?? 0;
    const safeCredit = creditedBalance ?? 0;

    const additionalFees = (scheduledPayments ?? []).filter(
        (sp) => sp.type !== ScheduledPaymentType.Standard && sp.type !== ScheduledPaymentType.Discount,
    );
    const additionalFeesSum = additionalFees.reduce((acc, sp) => acc + Number(sp.paymentAmount), 0);

    const hasAnyDeduction =
        safeCredit > 0 || safeMember > 0 || safeDiscount > 0 || additionalFees.length > 0;

    if (!hasAnyDeduction) {
        return null;
    }

    const originalPrice = safeGross + safeCredit + safeMember + safeDiscount - additionalFeesSum;

    const isCompact = variant === "compact";
    const fontSize = isCompact ? 12 : 13;
    const labelFontSize = isCompact ? 13 : 14;

    const row: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontSize,
        lineHeight: "20px",
    };
    const deduction: React.CSSProperties = {color: "#979ea5", fontSize: fontSize - 1};

    return (
        <div className={classNames(css.container, className)} style={{fontSize}}>
            <div className={css.label} style={{marginBottom: 2, fontSize: labelFontSize, paddingTop: isCompact ? 6 : 10}}>
                {"Preisübersicht"}
            </div>
            <div style={row}>
                <div>{"Buchungspreis (Original)"}</div>
                <RenderAmount amount={originalPrice} style={{fontSize}} />
            </div>
            {safeMember > 0 && (
                <div style={{...row, ...deduction}}>
                    <div>{"Mitgliederrabatt"}</div>
                    <div style={deduction}>
                        {"- "}
                        <RenderAmount amount={safeMember} style={{display: "inline", fontSize: fontSize - 1}} />
                    </div>
                </div>
            )}
            {safeDiscount > 0 && (
                <div style={{...row, ...deduction}}>
                    <div>{discountName || "Rabatt"}</div>
                    <div style={deduction}>
                        {"- "}
                        <RenderAmount amount={safeDiscount} style={{display: "inline", fontSize: fontSize - 1}} />
                    </div>
                </div>
            )}
            {safeCredit > 0 && (
                <div style={{...row, ...deduction}}>
                    <div>{"Verrechnetes Guthaben"}</div>
                    <div style={deduction}>
                        {"- "}
                        <RenderAmount amount={safeCredit} style={{display: "inline", fontSize: fontSize - 1}} />
                    </div>
                </div>
            )}
            {additionalFees.map((fee) => (
                <div key={fee.id} style={row}>
                    <div>{fee.title ?? fee.type}</div>
                    <div>
                        {"+ "}
                        <RenderAmount amount={fee.paymentAmount} style={{display: "inline", fontSize}} />
                    </div>
                </div>
            ))}
            <div style={{borderTop: "1px solid #e8e8e8", margin: "3px 0"}} />
            <div style={{...row, fontWeight: 600}}>
                <div>{i18n.containers.invoice.InvoiceDetails.bookingPrice()}</div>
                <RenderAmount amount={safeGross} style={{fontWeight: 600, fontSize}} />
            </div>
        </div>
    );
};

export default memo(BookingPriceOverview);
